/**
 * Locale - Singleton Publisher for internationalization and locale management
 *
 * SINGLETON + PUBLISHER: Manages available locales and publishes selected locale changes.
 * Integrates with multiple i18n services (LocalizeJS, Protecht, Stripe, Seats.io).
 *
 * PUBLIC API NOTE: Public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * PUBLISHER PATTERN:
 * - Maintains list of available locales
 * - Tracks currently selected locale
 * - Publishes changes to subscribers
 * - Integrates with external i18n services
 *
 * FEATURES:
 * - Locale detection and selection
 * - Multi-service integration (LocalizeJS, Protecht, Stripe, Seats.io)
 * - Language and region parsing
 * - Label deduplication
 * - User preference persistence
 * - Automatic browser locale detection
 *
 * @class Locale
 * @extends Publisher
 * @singleton
 *
 * @example
 * // Subscribe to locale changes
 * Locale.get({
 *     subscribe: function(localeData) {
 *         const { selected } = localeData;
 *         // Update UI when locale changes
 *     }
 * });
 */

/**
 * Locale object structure
 *
 * @typedef {Object} LocaleObject
 * @property {string} key - Alias for tixrKey
 * @property {string} tixrKey - Internal locale key (e.g., "en_US")
 * @property {string} label - Display name (e.g., "English")
 * @property {string} shortLabel - Abbreviated name (e.g., "EN")
 * @property {string} language - Language code (e.g., "en")
 * @property {string} region - Region code (e.g., "US")
 * @property {string} localizeJsKey - LocalizeJS locale identifier
 * @property {string} protechtKey - Protecht service locale
 * @property {string} seatsIoKey - Seats.io locale
 * @property {string} stripeKey - Stripe locale
 */
define("Locale", [`Publisher`, `module`], (Super, Self) => {
	const PREFERRED_LOCALE_STORAGE_KEY = `tixr-locale`;

	// Fallback translations for phrases that LocalizeJS cannot detect via DOM scanning.
	// These are server-rendered strings (e.g. from Java enums) that are injected programmatically
	// and never appear as static text nodes in the DOM for LocalizeJS's MutationObserver to find.
	const FALLBACK_TRANSLATIONS = {
		'es-MX': {
			FREE: `GRATIS`,
			Free: `Gratis`,
			'Full refund': `Reembolso completo`,
			'Partial refund': `Reembolso parcial`,
			'Ticket refund': `Reembolso de boleto`,
			'Partial ticket refund': `Reembolso parcial de boleto`,
			'Order protection fees refund': `Reembolso de tarifas de protección de pedido`,
			'Full refund (excluding order protection fees)': `Reembolso completo (excluyendo tarifas de protección de pedido)`,
			'Ticket refund (excluding order protection fees)': `Reembolso de boleto (excluyendo tarifas de protección de pedido)`,
			'Not Available': `No disponible`,
			Included: `Incluido`,
			'Equal Value': `Valor igual`,
		},
		fr: {
			FREE: `GRATUIT`,
			Free: `Gratuit`,
			'Full refund': `Remboursement complet`,
			'Partial refund': `Remboursement partiel`,
			'Ticket refund': `Remboursement de billet`,
			'Partial ticket refund': `Remboursement partiel de billet`,
			'Order protection fees refund': `Remboursement des frais de protection de commande`,
			'Full refund (excluding order protection fees)': `Remboursement complet (hors frais de protection de commande)`,
			'Ticket refund (excluding order protection fees)': `Remboursement de billet (hors frais de protection de commande)`,
			'Not Available': `Non disponible`,
			Included: `Inclus`,
			'Equal Value': `Valeur égale`,
		},
	};

	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		requires: [`Storage`, `XHR`],
		prepare(setup) {
			const { data, XHR } = this;

			this.assign(data, {
				geoCountryCode: null, // resolved from XHR country header, used for IP-based legal content
				isLabelDuplicatedMap: {}, // used to determine if a language label is duplicated, and needs to be appended with the region
				isShortLabelDuplicatedMap: {}, // used to determine if a language shortLabel (shortLabel) is duplicated, and needs to be appended with the region
			});

			// Set the fan's country from the IP request header once it arrives. whenCountryCode()
			// has no timeout, so only a real header sets it; until then geoCountryCode stays null,
			// which getLegalLinks treats as non-Mexico (US). A country arriving late corrects it and
			// re-publishes so subscribers (e.g. PageFooter) re-render legal links.
			XHR.whenCountryCode().then((countryCode) => {
				if (this.isString(countryCode, 2)) {
					data.geoCountryCode = countryCode;

					const { value } = data;

					if (value && value.selected) {
						this.update({ selected: this.assign({}, value.selected) });
					}
				}
			});

			const fallback = this.normalize({
				label: `English`,
				localizeJsKey: `en`,
				protechtKey: `en_US`,
				seatsIoKey: `en`,
				shortLabel: `EN`,
				stripeKey: `en`,
				tixrKey: `en_US`,
			});
			const { key: nativeLocaleKey } = fallback;
			const available = [fallback];
			const selected = fallback;

			this.assign(data, {
				detectionComplete: false,
				localizeValue: null, // used to avoid unnecessary api calls
				nativeLocaleKey, // used to avoid loading their api prematurely
				userDefault: null, // used to remember user default
			});

			this.update({ available, selected });
		},
		api: {
			/**
			 * Used by App.js to register any additional locales the site supports.
			 * @method Locale#normalize
			 * @public
			 * @param {Locale} locale
			 * @returns {Locale}
			 */
			normalize: {
				value(locale) {
					const { data } = this;
					const { tixrKey } = locale;
					const [language, region] = tixrKey.split(`_`);
					const { isLabelDuplicatedMap, isShortLabelDuplicatedMap } = data;

					return this.defaults(locale, {
						language,
						localizeJsKey: `en`,
						key: tixrKey, // convenience alias
						protechtKey: `en_US`,
						region,
						seatsIoKey: `en`,
						shortLabel: language.toUpperCase(),
						stripeKey: `auto`,
						uniqueLabel: isLabelDuplicatedMap[locale.label] ? `${locale.label} (${region})` : locale.label,
						uniqueShortLabel: isShortLabelDuplicatedMap[locale.shortLabel]
							? `${locale.shortLabel} (${region})`
							: locale.shortLabel,
					});
				},
			},

			/**
			 * Configure available locales and select best match.
			 *
			 * @method Locale#setAvailable
			 * @param {Object} options - Configuration
			 * @param {LocaleObject[]} options.available - Available locales
			 * @param {string} options.defaultKey - Fallback locale key
			 * @returns {Promise} Resolves when locale selected and published
			 */
			setAvailable: {
				public: true,
				async value(context, options) {
					const { data } = this;
					const {
						available, // list of available locales
						defaultKey, // default to use as a last resort
					} = options;
					const {
						detectionComplete, // whether detecting fan locale has been done
						value, // the published locale digest
						userDefault, // the user's default locale
					} = data;
					const { selected: existing } = value;
					let selected;

					this.generateLabelMaps({ available });

					// add local properties to each locale
					this.each(available, this.normalize);

					if (!detectionComplete) {
						// first run, check for a locale param in the URL hash
						await this.require({ deps: [`App`] });
						const { App } = this;
						const hashParams = App.getHashParams();
						const queryParams = App.getQueryParams();
						const { locale: hashUrlKey } = hashParams;
						const { locale: queryUrlKey } = queryParams;

						// find the associated locale; check hash first, then query param
						if (this.isString(hashUrlKey, 1)) {
							selected = this.find(available, { key: hashUrlKey });
						} else if (this.isString(queryUrlKey, 1)) {
							selected = this.find(available, { key: queryUrlKey });
						}
					}

					if (detectionComplete && this.isObject(existing)) {
						// There's an existing locale selection
						const { key: existingKey } = existing;
						// Make sure it's still present in the new list
						selected = this.find(available, { key: existingKey });
					}

					if (selected === undefined) {
						// no existing selection or existing is unsupported
						if (this.isString(userDefault)) {
							// a user default has been provided
							selected = this.find(available, { key: userDefault });
						}
					}

					if (selected === undefined) {
						// Signed-out preference from a previous visit / language picker choice
						const storedKey = this.getPreferredLocale();

						if (this.isString(storedKey, 1)) {
							selected = this.find(available, { key: storedKey });
						}
					}

					if (selected === undefined) {
						// no preferred user selection, look for full browser locale match
						const { navigator } = window;
						const { languages } = navigator;

						this.find(languages, (browserKey) => {
							const key = browserKey.replace(`-`, `_`);

							selected = this.find(available, { key });
							return this.isObject(selected);
						});

						if (selected === undefined) {
							// no full browser locale match, look for language match
							this.find(languages, (browserKey) => {
								const splitBrowserKey = browserKey.split(`-`);
								const key = this.last(splitBrowserKey);

								selected = this.find(available, { language: key });
								return this.isObject(selected);
							});
						}
					}

					if (selected === undefined) {
						// no hints from the user or browser, use the default
						selected = this.find(available, { key: defaultKey });
					}

					data.detectionComplete = true;
					this.update({ available, selected });
				},
			},

			/**
			 * Set selected locale by key.
			 *
			 * @method Locale#set
			 * @param {Object} options - Options
			 * @param {string} options.key - Locale key to select
			 * @param {boolean} [options.persist=true] - Persist to localStorage for signed-out revisit
			 */
			set: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { key, persist = true } = options;
					const { value } = data;
					const { available } = value;
					const selected = this.find(available, { key });

					if (!this.isObject(selected)) {
						return;
					}

					if (persist !== false) {
						this.persistPreferredLocale({ key });
					}

					this.update({ selected });
				},
			},

			/**
			 * Read the fan's preferred locale key from localStorage.
			 *
			 * @method Locale#getPreferredLocale
			 * @returns {?string}
			 */
			getPreferredLocale: {
				value() {
					const { Storage } = this;

					return Storage.get({ key: PREFERRED_LOCALE_STORAGE_KEY });
				},
			},

			/**
			 * Persist the fan's preferred locale key to localStorage.
			 * Shared across tabs so full-page legal links keep the selection when signed out.
			 *
			 * @method Locale#persistPreferredLocale
			 * @param {Object} options
			 * @param {string} options.key
			 */
			persistPreferredLocale: {
				value(options) {
					const { Storage } = this;
					const { key } = options;

					if (!this.isString(key, 1)) {
						return;
					}

					Storage.set({
						key: PREFERRED_LOCALE_STORAGE_KEY,
						value: key,
					});
				},
			},

			/**
			 * Set user's default locale preference.
			 *
			 * @method Locale#setUserDefault
			 * @param {Object} options - Options
			 * @param {string} options.key - Locale key to set as default
			 */
			setUserDefault: {
				public: true,
				value(context, options) {
					const { data } = this;
					const { key } = options;
					const { userDefault: existingDefault } = data;

					data.userDefault = key;

					if (this.isString(key) && key !== existingDefault) {
						const { value } = data;
						const { selected } = value;
						const { key: selectedKey } = selected;

						if (key !== selectedKey) {
							const locale = this.find(value.available, { key });

							if (this.isObject(locale)) {
								this.update({ selected: locale });
							}
						}
					}
				},
			},

			/**
			 * The locale reflecting the fan's own preference, which is not always the locale on screen:
			 * a page only offers the locales its event or group supports, so an English-only event
			 * displays in English even when the fan's account is set to French.
			 *
			 * Use this to resolve things about the fan (e.g. the country their checkout should default
			 * to) and Locale#get to translate what is on screen.
			 *
			 * @method Locale#getFanLocale
			 * @public
			 * @returns {{key: String, language: String, region: String}}
			 */
			getFanLocale: {
				public: true,
				value() {
					const { data } = this;
					const { userDefault } = data;
					const { available, selected } = this.get();
					// When this page offers their locale, the selection already reflects it or the
					// override they chose here, so only reach for the account default when it cannot.
					const isOffered = !this.isString(userDefault, 1) || this.isObject(this.find(available, { key: userDefault }));
					const key = isOffered ? selected.key : userDefault;
					const [language, region] = key.split(`_`);

					return { key, language, region };
				},
			},

			getLocalizeApi: {
				async: true,
				value() {
					const { Config, promises } = this;
					const { localize: localizePromise } = promises;

					if (this.isPromise(localizePromise)) {
						return localizePromise;
					}

					const deps = [`localize`];
					const promise = this.when({
						promise: this.require({ deps }),
						spread: async (Localize) => {
							const { ENVIRONMENT, LOCALIZEJS_PROJECT_KEY: key } = Config;
							const disableWidget = true; // we're using our own menu
							const rememberLanguage = false; // respect page default
							const autoApprove = ENVIRONMENT === `dev`;
							const retranslateOnNewPhrases = ENVIRONMENT === `dev`;

							await Localize.initialize({ autoApprove, disableWidget, key, rememberLanguage, retranslateOnNewPhrases });
							return Localize;
						},
					});

					promises.localize = promise;
					return promise;
				},
			},

			syncLocalize: {
				async: true,
				async value() {
					const { data } = this;
					const { value } = data;
					const { selected } = value;

					if (selected === null) {
						return;
					}

					const { localizeValue, nativeLocaleKey } = data;
					const { key, localizeJsKey } = selected;

					if (localizeJsKey === localizeValue) {
						return;
					}

					if (
						key !== nativeLocaleKey || // switching to non-native language
						this.isString(localizeValue, 1) // Localize has already been used
					) {
						data.localizeValue = localizeJsKey;

						const Localize = await this.getLocalizeApi();
						data.localizeApi = Localize;
						Localize.setLanguage(localizeJsKey);
					}
				},
			},

			/**
			 * Translate a phrase using the LocalizeJS API.
			 * Returns the translated string if LocalizeJS is loaded, otherwise returns the original phrase.
			 * Falls back to a built-in map for phrases that LocalizeJS cannot detect via DOM scanning
			 * (e.g. server-rendered strings injected programmatically before template rendering).
			 *
			 * @method Locale#translate
			 * @param {string} phrase - The source phrase to translate
			 * @param {Object} [variables] - Optional key-value pairs for variable substitution
			 * @returns {string} The translated phrase, or the original phrase if LocalizeJS is not loaded
			 */
			translate: {
				public: true,
				value(context, phrase, variables) {
					const { data } = this;
					const { localizeApi } = data;

					if (localizeApi) {
						const result = variables ? localizeApi.translate(phrase, variables) : localizeApi.translate(phrase);

						// If LocalizeJS returned the original phrase (not in dictionary), check fallback map
						if (result === phrase) {
							const { selected } = this.get();
							const langKey = this.isObject(selected) ? selected.localizeJsKey : null;
							const langMap = FALLBACK_TRANSLATIONS[langKey];

							if (langMap && langMap[phrase]) {
								return langMap[phrase];
							}
						}

						return result;
					}

					if (variables) {
						return phrase.replace(/%\{(\w+)\}/g, (match, key) => (key in variables ? variables[key] : match));
					}

					return phrase;
				},
			},

			/**
			 * generate duplicate label maps
			 */
			generateLabelMaps: {
				value(options) {
					const { data } = this;
					const { available } = options;
					data.isLabelDuplicatedMap = {};
					data.isShortLabelDuplicatedMap = {};

					this.each(available, ({ label, shortLabel }) => {
						data.isLabelDuplicatedMap[label] = data.isLabelDuplicatedMap[label] != null;
						data.isShortLabelDuplicatedMap[shortLabel] = data.isShortLabelDuplicatedMap[shortLabel] != null;
					});
				},
			},

			update: {
				value() {
					// attach to any adjustment of the of the internal value
					this.syncLocalize();
				},
			},

			/**
			 * Resolve Tixr legal document URLs for the current request. Mexico-specific
			 * content (routes + Spanish disclosure templates) is used only on a Mexican IP.
			 *
			 * @method Locale#getLegalLinks
			 * @returns {{privacyPolicyUrl: string, termsOfUseUrl: string, termsAndConditionsUrl: ?string, isMexico: boolean}}
			 */
			getLegalLinks: {
				public: true,
				value() {
					const { Config, data } = this;
					const { geoCountryCode } = data;
					const isMexico = geoCountryCode === `MX`;

					if (isMexico) {
						return this.assign({}, Config.MEXICO_LEGAL_LINKS, {
							isMexico: true,
						});
					}

					return this.assign({}, Config.DEFAULT_LEGAL_LINKS, {
						isMexico: false,
					});
				},
			},

			/**
			 * Open language selection overlay.
			 *
			 * @method Locale#loadLanguageSelectionOverlay
			 * @param {Object} [options] - Overlay options
			 * @param {Object} [options.callbacks] - Callback handlers
			 * @param {Function} [options.callbacks.change] - Called when locale changes
			 */
			loadLanguageSelectionOverlay: {
				public: true,
				value(context, options = {}) {
					const { data, App } = this;
					const { available, selected } = this.get();
					const { callbacks } = options;

					const inputs = {
						locale: {
							type: `RadioSelect`,
							label: `Choose a language`,
							rules: {
								options: available.map(({ key, uniqueLabel }) => {
									return {
										value: key,
										label: `${uniqueLabel}`,
									};
								}),
								layout: `buttons`,
								allowTranslation: false,
								allowDeselection: false,
							},
							validation: {
								type: `String`,
							},
						},
					};

					App.loadOverlay({
						type: `FormModal`,
						replace: false,
						setup: {
							opacity: 0.9,
							title: `Choose a language`,
							inputs,
							overlayMode: `modal`,
							version: `ux4`,
							style: `ux4`,
							size: `small`,
							addSubmitButton: false,
							theme: `LIGHT`,
							values: {
								locale: selected.key,
							},
							callbacks: {
								change: (value) => {
									this.set({ key: value });

									if (this.isObject(callbacks) && this.isFunction(callbacks.change)) {
										callbacks.change(value);
									}

									if (this.isFunction(data.languageSelectionOverlay.destroy)) {
										data.languageSelectionOverlay.destroy();
									}
								},
							},
						},
						then: (overlay) => {
							data.languageSelectionOverlay = overlay;
						},
					});
				},
			},
		},
	});
});

//# sourceMappingURL=Locale.js.map